/**
 * Générateur — Reconnaitre une situation de proportionnalité à partir d'un tableau ou d'un
 * graphique.
 * Attendu ID : 986 | Niveau : 5ème | Domaine : proportionnalite
 *
 * ═══════════════════════════════════════════════════════════════════
 * PLAN DE CARTES (version conforme à la spécification 986.json, 2026-09-21)
 * ═══════════════════════════════════════════════════════════════════
 *
 * Libellé officiel (spec.attendu) : « Reconnaitre une situation de proportionnalité à partir
 * d'un tableau ou d'un graphique. »
 *
 * Catégorie « reconn_tableau » (niveaux 1 et 2 : décider sur un tableau, puis justifier par un
 * quotient)
 *   - reconn_tableau_qcm_unique  QCM : lequel de ces quatre tableaux relève (ou ne relève pas)
 *   - reconn_tableau_vf          V/F : quatre affirmations sur un tableau donné
 *   - reconn_tableau_texte       Saisie : le quotient d'une colonne nommée (niveau 2)
 *   - reconn_tableau_relier      Relier : chaque tableau à son verdict et sa raison
 *
 * Catégorie « reconn_graphique » (niveau 3 : décider sur un graphique, puis sélectionner le
 * critère)
 *   - reconn_graphique_qcm_unique QCM : décision + critère (alignement, passage par l'origine)
 *   - reconn_graphique_vf         V/F : quatre affirmations sur un graphique donné
 *   - reconn_graphique_classer    Classer : quatre graphiques en deux colonnes
 *   - reconn_graphique_texte_trou Trous (drag) : compléter le critère qui justifie la décision
 *
 * Catégorie « reconn_mixte » (niveau 4 : quatre représentations, deux tableaux et deux
 * graphiques, dont les cas les plus trompeurs)
 *   - reconn_mixte_qcm_unique    QCM : laquelle des quatre relève de la proportionnalité
 *   - reconn_mixte_texte         Saisie : combien des quatre en relèvent
 *   - reconn_mixte_tableau_vf    V/F : une ligne par représentation
 *
 * ── Spécification appliquée ──
 * Bornes :
 *   - support_de_reconnaissance : tableau et graphique, les deux servis, chacun avec son critère.
 *   - nature_de_la_relation : les cinq natures — proportionnelle par multiplication,
 *     proportionnelle par division (coefficient inférieur à 1), affine, quadratique, quelconque.
 *   - nombre_de_couples_du_tableau : 3 ou 4, jamais 2 (`exclusions` 1).
 *   - valeur_de_la_premiere_ligne : [4, 12], entière ou au demi, valeurs deux à deux distinctes
 *     et resserrées.
 *   - coefficient_de_la_relation_proportionnelle : [0,25 ; 9] ; entier pour la forme
 *     multiplicative, inférieur à 1 pour la forme divisive ; la seconde ligne est exacte à une
 *     décimale au plus.
 *   - terme_constant_d_une_relation_affine : [2, 6].
 *   - nombre_de_points_du_graphique : 4 ou 5, abscisses distinctes et régulièrement espacées.
 *   - ordonnee_a_l_origine_du_graphique : nulle dans le cas proportionnel, d'au moins un carreau
 *     dans le cas affine (`exclusions` 3, `points_de_vigilance` 8).
 *   - part_de_situations_proportionnelles_dans_une_serie : 0,5 sur toutes les cartes à plusieurs
 *     représentations ; les QCM à réponse unique alternent à parité « laquelle relève » et
 *     « laquelle ne relève pas », ce qui ramène la moyenne de série à 0,5.
 * Exclusions respectées : jamais deux couples seulement, aucune valeur nulle ni négative, aucun
 *   graphique affine dont le décalage serait inférieur à un carreau, AUCUN point relié (les
 *   points sont marqués, jamais joints — `exclusions` 4 et `points_de_vigilance` 3), jamais
 *   toutes les représentations du même côté, aucun calcul au-delà de la comparaison des
 *   quotients.
 *
 * Erreurs visées → mots-clés (codes des distracteurs et des cartes qui les provoquent) :
 *   1. écart constant pris pour un rapport constant     → `ecart_constant_pris_pour_rapport`
 *   2. coefficient inférieur à 1 jugé non proportionnel → `coefficient_inferieur_a_un_rejete`
 *   3. valeurs décimales jugées non proportionnelles    → `decimaux_rejetes`
 *   4. points alignés sans passer par l'origine, jugés proportionnels
 *                                                        → `alignement_sans_origine`
 *   5. conclusion d'après les deux premières colonnes seulement
 *                                                        → `deux_colonnes_suffisent`
 *   6. croire que le résultat dépend du sens de lecture des quotients
 *                                                        → `sens_de_lecture_des_quotients`
 *
 * Graduation → cartes :
 *   niveau 1 → reconn_tableau_qcm_unique, reconn_tableau_vf
 *   niveau 2 → reconn_tableau_texte, reconn_tableau_relier
 *   niveau 3 → reconn_graphique_qcm_unique, reconn_graphique_vf, reconn_graphique_classer,
 *              reconn_graphique_texte_trou
 *   niveau 4 → reconn_mixte_qcm_unique, reconn_mixte_texte, reconn_mixte_tableau_vf
 *
 * Points non tranchés (voir audit/5eme/986.md) :
 *   - Le barème séparé décision / justification (`points_de_vigilance` 4, `ecarts_assumes` 2)
 *     suppose deux notes ; un payload n'en porte qu'une. La justification est donc soit intégrée
 *     à la proposition choisie (QCM de critère), soit portée par une carte dédiée (le quotient
 *     à saisir, le critère à compléter).
 *   - Le statut de chaque représentation est une DONNÉE produite avec la question
 *     (`_meta.params.natures`), jamais déduit de l'analyse d'un texte (`points_de_vigilance` 5).
 *
 * Invariants : arithmétique entière (centièmes de coefficient), points du graphique posés sur
 * des nœuds entiers du quadrillage, quotients d'un tableau non proportionnel distincts d'au
 * moins un dixième (décidabilité, `points_de_vigilance` 7), boucles bornées avec repli
 * déterministe, jamais de throw.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeRelier, makeTexteTrou, makeClasser,
} from '../../../utils';

const ATTENDU_ID = 986;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ─── Mots-clés des erreurs visées (spec 986.json) ───
export const ERREURS_VISEES = {
    ecart_constant_pris_pour_rapport: 'Juger proportionnel un tableau où l\'on passe d\'une ligne à l\'autre en ajoutant toujours le même nombre',
    coefficient_inferieur_a_un_rejete: 'Juger non proportionnel un tableau dont le coefficient est inférieur à 1, faute d\'y trouver un multiplicateur entier',
    decimaux_rejetes: 'Juger non proportionnel un tableau dont les valeurs sont décimales',
    alignement_sans_origine: 'Juger proportionnel un graphique dont les points sont alignés sans passer par l\'origine',
    deux_colonnes_suffisent: 'Conclure d\'après les deux premières colonnes seulement, sans vérifier la troisième',
    sens_de_lecture_des_quotients: 'Croire que le résultat dépend du sens dans lequel on calcule les quotients',
};

export const REGLES_DISTRACTEURS_QCM = { ...ERREURS_VISEES };

// ═══════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════

/** Écriture française d'un nombre exact (au plus deux décimales). */
const fr = (v) => {
    const arrondi = Math.round(v * 100) / 100;
    return String(arrondi).replace('.', ',');
};

const T_TH = 'background:#1E3A8A;color:#fff;padding:5px 12px;border:1px solid #64748b;font-weight:600';
const T_TD = 'padding:5px 12px;border:1px solid #64748b;text-align:center';
const ligneTable = (th, cells) => `<tr><th style="${T_TH}">${th}</th>`
    + cells.map((c) => `<td style="${T_TD}">${c}</td>`).join('') + '</tr>';
const tableHtml = (titre, t) => `<p style="text-align:center;margin:6px 0 0">${titre}</p>`
    + `<table style="border-collapse:collapse;margin:4px auto 12px">`
    + ligneTable(t.ctx.xEntete, t.xs.map(fr)) + ligneTable(t.ctx.yEntete, t.ys.map(fr))
    + '</table>';

// ═══════════════════════════════════════════════════════════
// CONTEXTES
// ═══════════════════════════════════════════════════════════

const CONTEXTES = [
    { id: 'prix', xEntete: 'masse (kg)', yEntete: 'prix (€)', xAxe: 'masse (kg)', yAxe: 'prix (€)' },
    { id: 'distance', xEntete: 'durée (h)', yEntete: 'distance (km)', xAxe: 'durée (h)', yAxe: 'distance (km)' },
    { id: 'recette', xEntete: 'nombre de parts', yEntete: 'masse de sucre (g)', xAxe: 'nombre de parts', yAxe: 'sucre (g)' },
    { id: 'tissu', xEntete: 'longueur (m)', yEntete: 'prix (€)', xAxe: 'longueur (m)', yAxe: 'prix (€)' },
    { id: 'peinture', xEntete: 'surface (m²)', yEntete: 'peinture (L)', xAxe: 'surface (m²)', yAxe: 'peinture (L)' },
];

// ═══════════════════════════════════════════════════════════
// TABLEAUX
// ═══════════════════════════════════════════════════════════

const COEFFS_MULT = [100, 200, 300, 400, 500, 600, 700, 800, 900];   // centièmes, entiers 1..9
const COEFFS_DIV = [25, 50, 75];                                      // centièmes, < 1
const VALEURS_LIGNE1 = [4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 9, 10, 11, 12];

/** Les quotients d'un tableau, en centièmes, pour la décidabilité. */
const quotients = (t) => t.xs.map((x, i) => Math.round((t.ys[i] / x) * 100));

/**
 * Tire un tableau de la nature demandée. Les valeurs de la première ligne sont
 * resserrées et distinctes ; la seconde ligne est exacte à une décimale au plus ;
 * pour un tableau NON proportionnel, les deux premières colonnes donnent le même
 * quotient et la dernière le casse (erreur visée 5), avec un écart décidable.
 */
const tirerTableau = (nature) => {
    for (let essai = 0; essai < 200; essai++) {
        const ctx = randomChoice(CONTEXTES);
        const n = randInt(3, 4);
        const xs = shuffle([...VALEURS_LIGNE1]).slice(0, n).sort((a, b) => a - b);
        if (new Set(xs).size !== n) continue;
        const kc = nature === 'prop_div' ? randomChoice(COEFFS_DIV) : randomChoice(COEFFS_MULT);
        if (xs.some((x) => (kc * x) % 10 !== 0)) continue;              // au plus une décimale
        const base = xs.map((x) => (kc * x) / 100);
        if (base.some((y) => y <= 0)) continue;                          // exclusion 2
        let ys = base;
        if (nature === 'affine') {
            const b = randInt(2, 6);
            ys = base.map((y, i) => (i < n - 1 ? y : y + b));            // la dernière casse la chaîne
        } else if (nature === 'quadratique') {
            // Le carré d'un demi n'est pas exact à une décimale : abscisses entières.
            if (xs.some((x) => !Number.isInteger(x))) continue;
            ys = xs.map((x) => x * x);
        } else if (nature === 'quelconque') {
            ys = base.map((y, i) => (i === n - 1 ? y + randInt(2, 6) : (i === 1 ? y + 1 : y)));
        }
        if (ys.some((y) => y <= 0 || Math.round(y * 10) !== y * 10)) continue;
        const t = { ctx, nature, xs, ys, coefficient: kc / 100, n };
        // Les quotients affichés doivent être EXACTS à deux décimales : un quotient
        // arrondi rendrait fausse l'affirmation qui le donne (V/F, relier, saisie).
        if (xs.some((x, i) => Math.abs((ys[i] / x) * 100 - Math.round((ys[i] / x) * 100)) > 1e-9)) continue;
        const qs = quotients(t);
        const proportionnel = nature === 'prop_mult' || nature === 'prop_div';
        if (proportionnel) {
            if (new Set(qs).size !== 1) continue;
        } else {
            // Décidabilité : les quotients qui diffèrent diffèrent d'au moins un dixième.
            const distincts = [...new Set(qs)];
            if (distincts.length < 2) continue;
            let ok = true;
            for (let i = 0; i < distincts.length; i++) {
                for (let j = i + 1; j < distincts.length; j++) {
                    if (Math.abs(distincts[i] - distincts[j]) < 10) ok = false;
                }
            }
            if (!ok) continue;
        }
        t.proportionnel = proportionnel;
        t.quotients = qs.map((q) => q / 100);
        return t;
    }
    const ctx = CONTEXTES[0];
    const t = {
        ctx, nature: 'prop_mult', xs: [4, 6, 9], ys: [12, 18, 27], coefficient: 3, n: 3,
        proportionnel: true, quotients: [3, 3, 3],
    };
    return t;
};

const NATURES_PROP = ['prop_mult', 'prop_div'];
const NATURES_NON_PROP = ['affine', 'quadratique', 'quelconque'];

const tirerTableauProportionnel = () => tirerTableau(randomChoice(NATURES_PROP));
const tirerTableauNonProportionnel = () => tirerTableau(randomChoice(NATURES_NON_PROP));

/** Le mot-clé d'erreur visée que sert un tableau, selon sa nature. */
const codeDeTableau = (t) => {
    if (t.nature === 'affine') return 'deux_colonnes_suffisent';
    if (t.nature === 'quadratique') return 'ecart_constant_pris_pour_rapport';
    if (t.nature === 'prop_div') return 'coefficient_inferieur_a_un_rejete';
    if (t.nature === 'prop_mult') return 'decimaux_rejetes';
    return 'ecart_constant_pris_pour_rapport';
};

/** Description textuelle d'un tableau (propositions de QCM et de relier). */
const descriptionTableau = (t) => `${t.ctx.xEntete} (${t.xs.map(fr).join(' ; ')}) et ${t.ctx.yEntete} (${t.ys.map(fr).join(' ; ')})`;

// ═══════════════════════════════════════════════════════════
// GRAPHIQUES (SVG : quadrillage, axes gradués et légendés,
// origine visible, points marqués et JAMAIS reliés)
// ═══════════════════════════════════════════════════════════

/**
 * Tire un graphique : quatre ou cinq points d'abscisses régulièrement espacées, tous
 * sur un nœud du quadrillage. Proportionnel = alignés ET passant par l'origine.
 */
const tirerGraphique = (proportionnel) => {
    for (let essai = 0; essai < 200; essai++) {
        const ctx = randomChoice(CONTEXTES);
        const n = randInt(4, 5);
        const kc = randomChoice([100, 150, 200, 250, 300]);               // pente, centièmes
        const nature = proportionnel
            ? randomChoice(['prop_mult', 'prop_div'])
            : randomChoice(['affine', 'quadratique', 'quelconque']);
        // Un coefficient inférieur à 1 demande un pas d'abscisse de 2 pour que les
        // ordonnées tombent sur des nœuds entiers du quadrillage.
        const xStep = nature === 'prop_div' ? 2 : 1;
        const pente = nature === 'prop_div' ? randomChoice([50, 75]) : kc;
        const xs = [];
        for (let i = 1; i <= n; i++) xs.push(i * xStep);
        let ys;
        let b = 0;
        if (proportionnel) {
            ys = xs.map((x) => (pente * x) / 100);
        } else if (nature === 'affine') {
            b = randInt(2, 5);                                            // au moins un carreau
            ys = xs.map((x) => (pente * x) / 100 + b);
        } else if (nature === 'quadratique') {
            ys = xs.map((x) => x * x);
        } else {
            ys = xs.map((x, i) => (pente * x) / 100 + (i % 2 === 0 ? 1 : 0) + (i === n - 1 ? 2 : 0));
        }
        if (ys.some((y) => !Number.isInteger(y) || y <= 0)) continue;
        if (nature === 'quelconque') {
            // Une relation quelconque ne doit ni être alignée ni passer par l'origine.
            const pentes = ys.map((y, i) => y / xs[i]);
            if (new Set(pentes).size < 3) continue;
        }
        const yMaxVal = Math.max(...ys);
        const yStep = [1, 2, 5, 10].find((p) => ys.every((y) => y % p === 0) && yMaxVal / p >= 4 && yMaxVal / p <= 10);
        if (!yStep) continue;
        if (!proportionnel && nature === 'affine' && b % yStep !== 0) continue;
        if (!proportionnel && nature === 'affine' && b / yStep < 1) continue;   // décalage ≥ 1 carreau
        const nx = n + 1;
        const ny = Math.round(yMaxVal / yStep) + 1;
        if (ny > 11) continue;
        return {
            ctx, nature, proportionnel, xs, ys, xStep, yStep, nx, ny,
            ordonneeOrigine: proportionnel ? 0 : (nature === 'affine' ? b : null),
        };
    }
    return {
        ctx: CONTEXTES[0], nature: 'prop_mult', proportionnel: true,
        xs: [1, 2, 3, 4], ys: [2, 4, 6, 8], xStep: 1, yStep: 2, nx: 5, ny: 5, ordonneeOrigine: 0,
    };
};

/** Un panneau de repère, en coordonnées entières (aucun nombre à virgule dans le SVG). */
const panneauSvg = (g, ox, oy, w, h, titre) => {
    const mL = 30; const mB = 24; const mT = titre ? 16 : 8; const mR = 10;
    const px = Math.max(10, Math.round((w - mL - mR) / g.nx));
    const py = Math.max(10, Math.round((h - mB - mT) / g.ny));
    const x0 = mL; const y0 = mT + g.ny * py;
    const X = (i) => x0 + i * px;
    const Y = (j) => y0 - j * py;
    let s = `<g transform="translate(${ox},${oy})">`;
    if (titre) s += `<text x="${x0 + (g.nx * px) / 2}" y="10" text-anchor="middle" font-size="11" fill="#1e293b">${titre}</text>`;
    for (let i = 0; i <= g.nx; i++) s += `<line x1="${X(i)}" y1="${Y(0)}" x2="${X(i)}" y2="${Y(g.ny)}" stroke="#cbd5e1" stroke-width="1"/>`;
    for (let j = 0; j <= g.ny; j++) s += `<line x1="${X(0)}" y1="${Y(j)}" x2="${X(g.nx)}" y2="${Y(j)}" stroke="#cbd5e1" stroke-width="1"/>`;
    s += `<line x1="${x0}" y1="${y0}" x2="${X(g.nx)}" y2="${y0}" stroke="#1e293b" stroke-width="2"/>`;
    s += `<line x1="${x0}" y1="${y0}" x2="${x0}" y2="${Y(g.ny)}" stroke="#1e293b" stroke-width="2"/>`;
    for (let i = 1; i <= g.nx; i++) s += `<text x="${X(i)}" y="${y0 + 12}" text-anchor="middle" font-size="9" fill="#475569">${i * g.xStep}</text>`;
    for (let j = 1; j <= g.ny; j++) s += `<text x="${x0 - 4}" y="${Y(j) + 3}" text-anchor="end" font-size="9" fill="#475569">${j * g.yStep}</text>`;
    s += `<text x="${x0 - 4}" y="${y0 + 12}" text-anchor="end" font-size="9" fill="#475569">0</text>`;
    s += `<text x="${X(g.nx)}" y="${y0 + 22}" text-anchor="end" font-size="9" fill="#1e293b">${g.ctx.xAxe}</text>`;
    s += `<text x="${x0 - 4}" y="${Y(g.ny) - 3}" text-anchor="start" font-size="9" fill="#1e293b">${g.ctx.yAxe}</text>`;
    g.xs.forEach((x, i) => {
        s += `<circle cx="${X(Math.round(x / g.xStep))}" cy="${Y(Math.round(g.ys[i] / g.yStep))}" r="3" fill="#1E3A8A"/>`;
    });
    s += '</g>';
    return s;
};

/** Une image (data-URI) portant un ou plusieurs panneaux de repère. */
const imageGraphiques = (graphiques, titres) => {
    const w = 210; const h = 175;
    const cols = graphiques.length > 1 ? 2 : 1;
    const rows = Math.ceil(graphiques.length / cols);
    const W = cols * w; const H = rows * h;
    const panneaux = graphiques.map((g, i) => panneauSvg(
        g, (i % cols) * w, Math.floor(i / cols) * h, w, h, titres ? titres[i] : null,
    )).join('');
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" font-family="Arial">`
        + `<rect x="0" y="0" width="${W}" height="${H}" fill="#ffffff"/>${panneaux}</svg>`;
    return {
        version: 1,
        elements: [{ type: 'image', config: { imageUrl: `data:image/svg+xml,${encodeURIComponent(svg)}`, maxWidth: graphiques.length > 1 ? 'medium' : 'small' } }],
    };
};

/** Les quatre critères de justification d'un graphique (type_reponse de la spec). */
const critereDe = (g) => {
    if (g.proportionnel) return 'Les points sont alignés et la droite qui les porte passe par l\'origine du repère.';
    if (g.nature === 'affine') return 'Les points sont alignés, mais la droite qui les porte ne passe pas par l\'origine du repère.';
    if (g.nature === 'quadratique') return 'Les points ne sont pas alignés.';
    return 'Les points ne sont pas alignés et ne partent pas de l\'origine du repère.';
};

const CRITERES = [
    'Les points sont alignés et la droite qui les porte passe par l\'origine du repère.',
    'Les points sont alignés, mais la droite qui les porte ne passe pas par l\'origine du repère.',
    'Les points ne sont pas alignés.',
    'Les points ne sont pas alignés et ne partent pas de l\'origine du repère.',
];

/** Le mot-clé d'erreur visée servi par chaque critère choisi à tort. */
const CODE_DE_CRITERE = [
    'alignement_sans_origine',
    'alignement_sans_origine',
    'ecart_constant_pris_pour_rapport',
    'ecart_constant_pris_pour_rapport',
];

const codeDeGraphique = (g) => {
    if (g.nature === 'affine') return 'alignement_sans_origine';
    if (g.nature === 'quadratique') return 'ecart_constant_pris_pour_rapport';
    if (g.nature === 'prop_div') return 'coefficient_inferieur_a_un_rejete';
    return 'alignement_sans_origine';
};

// ═══════════════════════════════════════════════════════════
// CATÉGORIE 1 — TABLEAUX (niveaux 1 et 2)
// ═══════════════════════════════════════════════════════════

const genTabQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const chercheProportionnel = randInt(0, 1) === 0;
        const tableaux = chercheProportionnel
            ? [tirerTableauProportionnel(), tirerTableauNonProportionnel(), tirerTableauNonProportionnel(), tirerTableauNonProportionnel()]
            : [tirerTableauNonProportionnel(), tirerTableauProportionnel(), tirerTableauProportionnel(), tirerTableauProportionnel()];
        const cible = tableaux[0];
        const textes = tableaux.map(descriptionTableau);
        if (new Set(textes).size !== 4) continue;
        const distracteurs = tableaux.slice(1).map((t, i) => ({ code: codeDeTableau(t), valeur: textes[i + 1] }));
        const consigne = chercheProportionnel
            ? '<p>Un seul de ces tableaux est un tableau de proportionnalité. Lequel ?</p>'
            : '<p>Un seul de ces tableaux n\'est pas un tableau de proportionnalité. Lequel ?</p>';
        const payload = makeQcm(textes, textes[0], consigne, opts('Reconnaître le tableau de proportionnalité'));
        payload._meta.params = {
            niveau: 1, support: 'tableau', sens: chercheProportionnel ? 'relever' : 'exclure',
            natures: tableaux.map((t) => t.nature),
            proportionnels: tableaux.map((t) => t.proportionnel),
            couples: cible.n, reponse: textes[0], distracteurs,
        };
        return payload;
    }
    const t = tirerTableauProportionnel();
    const autres = [tirerTableauNonProportionnel(), tirerTableauNonProportionnel(), tirerTableauNonProportionnel()];
    const textes = [t, ...autres].map(descriptionTableau);
    const payload = makeQcm(textes, textes[0],
        '<p>Un seul de ces tableaux est un tableau de proportionnalité. Lequel ?</p>',
        opts('Reconnaître le tableau de proportionnalité'));
    payload._meta.params = {
        niveau: 1, support: 'tableau', sens: 'relever',
        natures: [t, ...autres].map((x) => x.nature),
        proportionnels: [t, ...autres].map((x) => x.proportionnel),
        couples: t.n, reponse: textes[0],
        distracteurs: autres.map((x, i) => ({ code: codeDeTableau(x), valeur: textes[i + 1] })),
    };
    return payload;
};

const genTabVF = () => {
    const t = randInt(0, 1) === 0 ? tirerTableauProportionnel() : tirerTableauNonProportionnel();
    const q0 = t.quotients[0];
    const qDernier = t.quotients[t.n - 1];
    const plan = shuffle([true, true, false, false]);
    const distracteurs = [];
    const lignes = [
        // Statut : DONNÉE produite avec la question, jamais déduite d'un texte.
        plan[0] === t.proportionnel
            ? 'Ce tableau est un tableau de proportionnalité'
            : 'Ce tableau n\'est pas un tableau de proportionnalité',
        plan[1]
            ? `Le quotient de la première colonne vaut ${fr(q0)}`
            : `Le quotient de la première colonne vaut ${fr(q0 + 1)}`,
        plan[2]
            ? `Le quotient de la dernière colonne vaut ${fr(qDernier)}`
            : `Le quotient de la dernière colonne vaut ${fr(qDernier + 1)}`,
        plan[3]
            ? 'Pour décider, il faut comparer les quotients de toutes les colonnes'
            : 'Pour décider, il suffit de regarder les deux premières colonnes',
    ];
    const bonnes = [
        plan[0] ? 0 : 1,
        plan[1] ? 0 : 1,
        plan[2] ? 0 : 1,
        plan[3] ? 0 : 1,
    ];
    if (!plan[1]) distracteurs.push({ code: 'sens_de_lecture_des_quotients', valeur: fr(q0 + 1) });
    if (!plan[2]) distracteurs.push({ code: 'decimaux_rejetes', valeur: fr(qDernier + 1) });
    if (!plan[3]) distracteurs.push({ code: 'deux_colonnes_suffisent', valeur: 'deux premières colonnes' });
    const payload = makeVraiFaux(
        lignes, bonnes,
        `${tableHtml('Tableau étudié', t)}`
        + '<p>Pour chaque affirmation sur ce tableau, indique si elle est vraie ou fausse.</p>',
        opts('Vrai/Faux sur un tableau'),
    );
    payload._meta.params = {
        niveau: 1, support: 'tableau', natures: [t.nature], proportionnels: [t.proportionnel],
        couples: t.n, quotients: t.quotients, distracteurs,
    };
    return payload;
};

const genTabSaisie = () => {
    const t = randInt(0, 1) === 0 ? tirerTableauProportionnel() : tirerTableauNonProportionnel();
    const rang = t.n;                                  // la dernière colonne, celle qui décide
    const q = t.quotients[rang - 1];
    const payload = makeSaisie(
        fr(q),
        `${tableHtml('Tableau étudié', t)}`
        + `<p>Calcule le quotient de la seconde ligne par la première pour la colonne ${rang}, celle de ${fr(t.xs[rang - 1])}. `
        + 'Écris seulement le nombre. Tu pourras ensuite le comparer aux autres colonnes pour décider.</p>',
        { ...opts('Calculer le quotient d\'une colonne'), strict: false },
    );
    payload._meta.params = {
        niveau: 2, support: 'tableau', natures: [t.nature], proportionnels: [t.proportionnel],
        couples: t.n, quotients: t.quotients, reponse: fr(q),
        erreurVisee: 'deux_colonnes_suffisent',
    };
    return payload;
};

const genTabRelier = () => {
    for (let essai = 0; essai < 60; essai++) {
        const tableaux = shuffle([
            tirerTableauProportionnel(), tirerTableauProportionnel(),
            tirerTableauNonProportionnel(), tirerTableauNonProportionnel(),
        ]);
        const sources = tableaux.map(descriptionTableau);
        const targets = tableaux.map((t) => (t.proportionnel
            ? `Tableau de proportionnalité, tous les quotients valent ${fr(t.quotients[0])}`
            : `Pas un tableau de proportionnalité, le quotient passe de ${fr(t.quotients[0])} à ${fr(t.quotients[t.n - 1])}`));
        if (new Set(sources).size !== 4 || new Set(targets).size !== 4) continue;
        const payload = makeRelier(
            tableaux.map((t, i) => ({ source: sources[i], target: targets[i] })),
            '<p>Relie chaque tableau à la conclusion qui lui correspond.</p>',
            opts('Relier un tableau et sa conclusion'),
        );
        payload._meta.params = {
            niveau: 2, support: 'tableau', natures: tableaux.map((t) => t.nature),
            proportionnels: tableaux.map((t) => t.proportionnel), couples: tableaux[0].n,
            erreursVisees: ['sens_de_lecture_des_quotients'],
        };
        return payload;
    }
    const tableaux = [tirerTableauProportionnel(), tirerTableauNonProportionnel(), tirerTableauProportionnel(), tirerTableauNonProportionnel()];
    const payload = makeRelier(
        tableaux.map((t, i) => ({
            source: `Tableau ${i + 1}, ${descriptionTableau(t)}`,
            target: t.proportionnel
                ? `Le tableau ${i + 1} est un tableau de proportionnalité`
                : `Le tableau ${i + 1} n'est pas un tableau de proportionnalité`,
        })),
        '<p>Relie chaque tableau à la conclusion qui lui correspond.</p>',
        opts('Relier un tableau et sa conclusion'),
    );
    payload._meta.params = {
        niveau: 2, support: 'tableau', natures: tableaux.map((t) => t.nature),
        proportionnels: tableaux.map((t) => t.proportionnel), couples: tableaux[0].n,
        erreursVisees: ['sens_de_lecture_des_quotients'],
    };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// CATÉGORIE 2 — GRAPHIQUES (niveau 3)
// ═══════════════════════════════════════════════════════════

const genGraphQcm = () => {
    const g = tirerGraphique(randInt(0, 1) === 0);
    const bonne = critereDe(g);
    const distracteurs = CRITERES.filter((c) => c !== bonne).map((c) => ({
        code: CODE_DE_CRITERE[CRITERES.indexOf(c)], valeur: c,
    }));
    const payload = makeQcm(
        CRITERES, bonne,
        `<p>Le graphique ci-dessous représente ${g.ctx.yAxe} en fonction de ${g.ctx.xAxe}. Les points ne sont pas reliés.</p>`
        + '<p>Quelle description correspond à ce graphique ?</p>',
        opts('Reconnaître la proportionnalité sur un graphique'),
    );
    payload.elements_additionnels = imageGraphiques([g]);
    payload._meta.params = {
        niveau: 3, support: 'graphique', natures: [g.nature], proportionnels: [g.proportionnel],
        points: g.xs.length, ordonneeOrigine: g.ordonneeOrigine, yStep: g.yStep,
        reponse: bonne, distracteurs,
    };
    return payload;
};

const genGraphVF = () => {
    const g = tirerGraphique(randInt(0, 1) === 0);
    const aligne = g.nature !== 'quadratique' && g.nature !== 'quelconque';
    const plan = shuffle([true, true, false, false]);
    const distracteurs = [];
    const lignes = [
        plan[0] === g.proportionnel
            ? 'Ce graphique représente une situation de proportionnalité'
            : 'Ce graphique ne représente pas une situation de proportionnalité',
        plan[1] === aligne
            ? 'Les points de ce graphique sont alignés'
            : 'Les points de ce graphique ne sont pas alignés',
        plan[2] === g.proportionnel
            ? 'Ces points sont alignés avec l\'origine du repère'
            : 'Ces points ne sont pas alignés avec l\'origine du repère',
        plan[3]
            ? 'Pour être proportionnel, un graphique doit avoir ses points alignés ET passer par l\'origine'
            : 'Pour être proportionnel, il suffit que les points d\'un graphique soient alignés',
    ];
    if (!plan[3]) distracteurs.push({ code: 'alignement_sans_origine', valeur: 'il suffit que les points soient alignés' });
    if (!plan[1]) distracteurs.push({ code: 'ecart_constant_pris_pour_rapport', valeur: 'alignement' });
    if (!plan[2]) distracteurs.push({ code: 'alignement_sans_origine', valeur: 'origine' });
    const payload = makeVraiFaux(
        lignes, plan.map((x) => (x ? 0 : 1)),
        `<p>Le graphique ci-dessous représente ${g.ctx.yAxe} en fonction de ${g.ctx.xAxe}. Les points ne sont pas reliés.</p>`
        + '<p>Pour chaque affirmation, indique si elle est vraie ou fausse.</p>',
        opts('Vrai/Faux sur un graphique'),
    );
    payload.elements_additionnels = {
        version: 1,
        elements: [...imageGraphiques([g]).elements, ...payload.elements_additionnels.elements],
    };
    payload._meta.params = {
        niveau: 3, support: 'graphique', natures: [g.nature], proportionnels: [g.proportionnel],
        points: g.xs.length, ordonneeOrigine: g.ordonneeOrigine, yStep: g.yStep, distracteurs,
    };
    return payload;
};

const genGraphClasser = () => {
    const graphiques = shuffle([
        tirerGraphique(true), tirerGraphique(true), tirerGraphique(false), tirerGraphique(false),
    ]);
    const titres = graphiques.map((g, i) => `Graphique ${i + 1}`);
    const categories = [
        { name: 'Situation de proportionnalité', items: titres.filter((_, i) => graphiques[i].proportionnel) },
        { name: 'Pas une situation de proportionnalité', items: titres.filter((_, i) => !graphiques[i].proportionnel) },
    ];
    const payload = makeClasser(
        categories,
        '<p>Quatre graphiques sont représentés ci-dessous, points non reliés.</p>'
        + '<p>Classe chaque graphique selon qu\'il représente ou non une situation de proportionnalité.</p>',
        opts('Classer quatre graphiques'),
    );
    payload.elements_additionnels = imageGraphiques(graphiques, titres);
    payload._meta.params = {
        niveau: 3, support: 'graphique', natures: graphiques.map((g) => g.nature),
        proportionnels: graphiques.map((g) => g.proportionnel), points: graphiques[0].xs.length,
        erreursVisees: ['alignement_sans_origine'],
    };
    return payload;
};

const genGraphTrou = () => {
    const g = tirerGraphique(randInt(0, 1) === 0);
    const aligne = g.nature !== 'quadratique' && g.nature !== 'quelconque';
    const motAlignes = aligne ? 'alignés' : 'non alignés';
    const motOrigine = g.proportionnel ? 'passe par l\'origine' : 'ne passe pas par l\'origine';
    const motVerdict = g.proportionnel ? 'proportionnalité' : 'pas de proportionnalité';
    const phrase = `Sur ce graphique, les points sont {{${motAlignes}}}\n`
        + `La droite qui les porterait {{${motOrigine}}}\n`
        + `La conclusion est donc {{${motVerdict}}}`;
    const distracteurs = [
        aligne ? 'non alignés' : 'alignés',
        g.proportionnel ? 'ne passe pas par l\'origine' : 'passe par l\'origine',
        g.proportionnel ? 'pas de proportionnalité' : 'proportionnalité',
    ];
    const payload = makeTexteTrou(
        phrase,
        `<p>Le graphique ci-dessous représente ${g.ctx.yAxe} en fonction de ${g.ctx.xAxe}. Les points ne sont pas reliés.</p>`
        + '<p>Complète la justification avec les bons mots.</p>',
        { ...opts('Compléter le critère de reconnaissance'), mode: 'drag', distracteurs },
    );
    payload.elements_additionnels = imageGraphiques([g]);
    payload._meta.params = {
        niveau: 3, support: 'graphique', natures: [g.nature], proportionnels: [g.proportionnel],
        points: g.xs.length, ordonneeOrigine: g.ordonneeOrigine, yStep: g.yStep,
        distracteurs: [{ code: 'alignement_sans_origine', valeur: distracteurs[1] }],
    };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// CATÉGORIE 3 — QUATRE REPRÉSENTATIONS (niveau 4)
// ═══════════════════════════════════════════════════════════

/**
 * Deux tableaux et deux graphiques, dont `nbProp` relevant de la proportionnalité.
 * Les cas les plus trompeurs sont privilégiés : coefficient inférieur à 1 côté
 * proportionnel, relation affine côté non proportionnel (graduation niveau 4).
 */
const tirerMixte = (nbProp) => {
    const plan = shuffle([0, 1, 2, 3].map((i) => i < nbProp));
    const tableau = (prop) => (prop
        ? tirerTableau(randInt(0, 2) === 0 ? 'prop_mult' : 'prop_div')
        : tirerTableau(randInt(0, 2) === 0 ? randomChoice(['quadratique', 'quelconque']) : 'affine'));
    const tableaux = [tableau(plan[0]), tableau(plan[1])];
    const graphiques = [tirerGraphique(plan[2]), tirerGraphique(plan[3])];
    return { tableaux, graphiques, plan };
};

const enonceMixte = (m) => `${tableHtml('Tableau 1', m.tableaux[0])}${tableHtml('Tableau 2', m.tableaux[1])}`;

const genMixteQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const chercheProportionnel = randInt(0, 1) === 0;
        const m = tirerMixte(chercheProportionnel ? 1 : 3);
        const items = [
            { nom: 'Le tableau 1', prop: m.tableaux[0].proportionnel, code: codeDeTableau(m.tableaux[0]) },
            { nom: 'Le tableau 2', prop: m.tableaux[1].proportionnel, code: codeDeTableau(m.tableaux[1]) },
            { nom: 'Le graphique 1', prop: m.graphiques[0].proportionnel, code: codeDeGraphique(m.graphiques[0]) },
            { nom: 'Le graphique 2', prop: m.graphiques[1].proportionnel, code: codeDeGraphique(m.graphiques[1]) },
        ];
        const cibles = items.filter((it) => it.prop === chercheProportionnel);
        if (cibles.length !== 1) continue;
        const bonne = cibles[0].nom;
        const distracteurs = items.filter((it) => it.nom !== bonne).map((it) => ({ code: it.code, valeur: it.nom }));
        const consigne = chercheProportionnel
            ? '<p>Une seule de ces quatre représentations représente une situation de proportionnalité. Laquelle ?</p>'
            : '<p>Une seule de ces quatre représentations ne représente pas une situation de proportionnalité. Laquelle ?</p>';
        const payload = makeQcm(
            items.map((it) => it.nom), bonne,
            `<p>Voici deux tableaux et deux graphiques, points non reliés.</p>${enonceMixte(m)}${consigne}`,
            opts('Reconnaître parmi quatre représentations'),
        );
        payload.elements_additionnels = imageGraphiques(m.graphiques, ['Graphique 1', 'Graphique 2']);
        payload._meta.params = {
            niveau: 4, support: 'mixte', sens: chercheProportionnel ? 'relever' : 'exclure',
            natures: [...m.tableaux.map((t) => t.nature), ...m.graphiques.map((g) => g.nature)],
            proportionnels: [...m.tableaux.map((t) => t.proportionnel), ...m.graphiques.map((g) => g.proportionnel)],
            couples: m.tableaux[0].n, points: m.graphiques[0].xs.length,
            reponse: bonne, distracteurs,
        };
        return payload;
    }
    const m = tirerMixte(1);
    const noms = ['Le tableau 1', 'Le tableau 2', 'Le graphique 1', 'Le graphique 2'];
    const props = [m.tableaux[0].proportionnel, m.tableaux[1].proportionnel, m.graphiques[0].proportionnel, m.graphiques[1].proportionnel];
    const idx = props.indexOf(true);
    const payload = makeQcm(noms, noms[idx],
        `<p>Voici deux tableaux et deux graphiques, points non reliés.</p>${enonceMixte(m)}`
        + '<p>Une seule de ces quatre représentations représente une situation de proportionnalité. Laquelle ?</p>',
        opts('Reconnaître parmi quatre représentations'));
    payload.elements_additionnels = imageGraphiques(m.graphiques, ['Graphique 1', 'Graphique 2']);
    payload._meta.params = {
        niveau: 4, support: 'mixte', sens: 'relever',
        natures: [...m.tableaux.map((t) => t.nature), ...m.graphiques.map((g) => g.nature)],
        proportionnels: props, couples: m.tableaux[0].n, points: m.graphiques[0].xs.length,
        reponse: noms[idx],
        distracteurs: noms.filter((_, i) => i !== idx).map((n, i) => ({ code: 'deux_colonnes_suffisent', valeur: n })),
    };
    return payload;
};

const genMixteSaisie = () => {
    const m = tirerMixte(2);
    const props = [
        m.tableaux[0].proportionnel, m.tableaux[1].proportionnel,
        m.graphiques[0].proportionnel, m.graphiques[1].proportionnel,
    ];
    const payload = makeSaisie(
        String(props.filter(Boolean).length),
        `<p>Voici deux tableaux et deux graphiques, points non reliés.</p>${enonceMixte(m)}`
        + '<p>Combien de ces quatre représentations représentent une situation de proportionnalité ? Écris le nombre.</p>',
        { ...opts('Compter les représentations proportionnelles'), strict: true },
    );
    payload.elements_additionnels = imageGraphiques(m.graphiques, ['Graphique 1', 'Graphique 2']);
    payload._meta.params = {
        niveau: 4, support: 'mixte',
        natures: [...m.tableaux.map((t) => t.nature), ...m.graphiques.map((g) => g.nature)],
        proportionnels: props, couples: m.tableaux[0].n, points: m.graphiques[0].xs.length,
        reponse: String(props.filter(Boolean).length), erreurVisee: 'deux_colonnes_suffisent',
    };
    return payload;
};

const genMixteVF = () => {
    const m = tirerMixte(2);
    const items = [
        { nom: 'Le tableau 1', prop: m.tableaux[0].proportionnel },
        { nom: 'Le tableau 2', prop: m.tableaux[1].proportionnel },
        { nom: 'Le graphique 1', prop: m.graphiques[0].proportionnel },
        { nom: 'Le graphique 2', prop: m.graphiques[1].proportionnel },
    ];
    // Le statut affiché est tiré ; la colonne juste est la DONNÉE de fabrication.
    const affirme = shuffle([true, true, false, false]);
    const lignes = items.map((it, i) => (affirme[i]
        ? `${it.nom} représente une situation de proportionnalité`
        : `${it.nom} ne représente pas une situation de proportionnalité`));
    const bonnes = items.map((it, i) => (affirme[i] === it.prop ? 0 : 1));
    const payload = makeVraiFaux(
        lignes, bonnes,
        `<p>Voici deux tableaux et deux graphiques, points non reliés.</p>${enonceMixte(m)}`
        + '<p>Pour chaque affirmation, indique si elle est vraie ou fausse.</p>',
        opts('Vrai/Faux sur quatre représentations'),
    );
    payload.elements_additionnels = {
        version: 1,
        elements: [
            ...imageGraphiques(m.graphiques, ['Graphique 1', 'Graphique 2']).elements,
            ...payload.elements_additionnels.elements,
        ],
    };
    payload._meta.params = {
        niveau: 4, support: 'mixte',
        natures: [...m.tableaux.map((t) => t.nature), ...m.graphiques.map((g) => g.nature)],
        proportionnels: items.map((it) => it.prop), couples: m.tableaux[0].n,
        points: m.graphiques[0].xs.length,
        distracteurs: [{ code: 'coefficient_inferieur_a_un_rejete', valeur: 'tableau à coefficient inférieur à 1' }],
    };
    return payload;
};

// ─── Registre ───
const GENERATORS = {
    reconn_tableau_qcm_unique: genTabQcm,
    reconn_tableau_vf: genTabVF,
    reconn_tableau_texte: genTabSaisie,
    reconn_tableau_relier: genTabRelier,

    reconn_graphique_qcm_unique: genGraphQcm,
    reconn_graphique_vf: genGraphVF,
    reconn_graphique_classer: genGraphClasser,
    reconn_graphique_texte_trou: genGraphTrou,

    reconn_mixte_qcm_unique: genMixteQcm,
    reconn_mixte_texte: genMixteSaisie,
    reconn_mixte_tableau_vf: genMixteVF,
};

export const CATEGORIES = [
    {
        id: 'reconn_tableau',
        label: 'Reconnaître la proportionnalité dans un tableau',
        formatsList: [
            { key: 'reconn_tableau_qcm_unique', label: 'QCM' },
            { key: 'reconn_tableau_vf', label: 'Vrai/Faux' },
            { key: 'reconn_tableau_texte', label: 'Saisie' },
            { key: 'reconn_tableau_relier', label: 'Relier' },
        ],
    },
    {
        id: 'reconn_graphique',
        label: 'Reconnaître la proportionnalité dans un graphique',
        formatsList: [
            { key: 'reconn_graphique_qcm_unique', label: 'QCM' },
            { key: 'reconn_graphique_vf', label: 'Vrai/Faux' },
            { key: 'reconn_graphique_classer', label: 'Classer' },
            { key: 'reconn_graphique_texte_trou', label: 'Texte à trous' },
        ],
    },
    {
        id: 'reconn_mixte',
        label: 'Quatre représentations, deux tableaux et deux graphiques',
        formatsList: [
            { key: 'reconn_mixte_qcm_unique', label: 'QCM' },
            { key: 'reconn_mixte_texte', label: 'Saisie' },
            { key: 'reconn_mixte_tableau_vf', label: 'Vrai/Faux' },
        ],
    },
];

// ─── Point d'entrée ───
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
